import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { GlassMasthead } from "@/components/GlassMasthead";
import { MagazinePage, PAGE_W, PAGE_H } from "@/components/magazine/MagazinePage";
import { LangMenu } from "@/components/LangMenu";

const ease = [0.16, 1, 0.3, 1];

const Lines = ({ text }) => {
  const words = text.toUpperCase().split(" ");
  const lines = words.length >= 4 ? [words.slice(0, 2).join(" "), words.slice(2, 3).join(" "), words.slice(3).join(" ")] : words;
  return lines.map((l, i) => (
    <span key={i} className="line-mask">
      <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1.1, ease, delay: 0.55 + i * 0.13 }}>{l}</motion.span>
    </span>
  ));
};

const CoverCard = ({ cover }) => {
  const ref = useRef(null);
  const mx = useMotionValue(0), my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 120, damping: 18 });
  const scale = 0.62;
  return (
    <motion.div
      ref={ref}
      className="relative"
      style={{ width: PAGE_W * scale, height: PAGE_H * scale, rotateX: rx, rotateY: ry, transformStyle: "preserve-3d", perspective: 1400 }}
      onMouseMove={(e) => { const r = ref.current.getBoundingClientRect(); mx.set((e.clientX - r.left) / r.width - 0.5); my.set((e.clientY - r.top) / r.height - 0.5); }}
      onMouseLeave={() => { mx.set(0); my.set(0); }}
      initial={{ opacity: 0, y: 60, rotateY: -18 }}
      animate={{ opacity: 1, y: 0, rotateY: 0 }}
      transition={{ duration: 1.4, ease, delay: 0.3 }}
      data-testid="hero-cover-card"
    >
      <div className="absolute inset-0" style={{ boxShadow: "0 60px 120px -30px rgba(0,0,0,0.8), 0 0 0 1px rgba(217,218,221,0.15)" }}>
        <div style={{ width: PAGE_W, height: PAGE_H, transform: `scale(${scale})`, transformOrigin: "0 0" }}>
          {cover ? <MagazinePage page={cover} /> : <img src="/assets/cover_plate.jpg" alt="ROOTS cover" style={{ width: PAGE_W, height: PAGE_H, objectFit: "cover" }} />}
        </div>
      </div>
      <div className="absolute -inset-6 -z-10" style={{ background: "radial-gradient(60% 60% at 50% 60%, rgba(101,13,36,0.45), transparent 70%)", filter: "blur(30px)" }} />
    </motion.div>
  );
};

export const Hero = ({ edition, cover, lang, onLang }) => (
  <section className="relative min-h-screen overflow-hidden grain" data-testid="hero-section">
    <div className="absolute inset-0" style={{ background: "radial-gradient(90% 70% at 80% 30%, #3a3a3d 0%, #232324 40%, #191919 75%)" }} />
    <div className="absolute inset-x-0 top-0 h-[1px] silver-rule" />
    <header className="relative z-30 flex items-center justify-between px-6 sm:px-12 py-6">
      <div className="reader-label" style={{ color: "#fff" }}>ROOTS <span style={{ opacity: 0.5 }}>· {edition?.number || "EDITION 001"}</span></div>
      <div className="flex items-center gap-4">
        <LangMenu lang={lang} onLang={onLang} label={edition?.ui?.language} testid="landing-lang" />
        <Link to={`/read/${lang}`} className="btn-silver hidden sm:inline-flex" data-testid="hero-nav-read">{edition?.ui?.read || "Read"}</Link>
      </div>
    </header>

    <div className="relative z-10 grid lg:grid-cols-12 gap-10 px-6 sm:px-12 pt-6 pb-24 items-center">
      <div className="lg:col-span-7">
        <motion.div initial={{ opacity: 0, scale: 0.94, y: 24 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 1.3, ease }}>
          <GlassMasthead text="ROOTS" dark className="text-[22vw] lg:text-[13.5vw]" style={{ lineHeight: 0.82 }} />
        </motion.div>
        <motion.p className="font-head italic mt-4" style={{ fontSize: 22, color: "var(--platinum)" }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5, duration: 1 }} data-testid="hero-tagline">
          {edition?.tagline || "Rooted in Africa. Shaping Culture."}
        </motion.p>
        <div className="silver-rule my-10" style={{ opacity: 0.5 }} />
        <div className="reader-label" style={{ color: "var(--platinum)", opacity: 0.7 }}>{edition?.number || "EDITION 001"} · {edition?.month || "October 2026"}</div>
        <h1 className="font-head font-bold text-white mt-4 text-4xl sm:text-5xl lg:text-6xl" style={{ lineHeight: 0.92, letterSpacing: "-0.02em", fontSize: "clamp(48px, 7.5vw, 116px)" }} data-testid="hero-headline">
          <Lines text={edition?.title || "The New African Wave"} />
        </h1>
        <motion.p className="font-head italic mt-6 max-w-xl" style={{ fontSize: 20, color: "var(--platinum)", opacity: 0.85 }} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 0.85, y: 0 }} transition={{ delay: 1.1, duration: 0.9 }}>
          {edition?.deck || "Meet the voices, faces and creators shaping African culture."}
        </motion.p>
        <motion.div className="mt-10 flex flex-wrap items-center gap-4" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 0.8 }}>
          <Link to={`/read/${lang}`} className="btn-bordeaux" style={{ padding: "16px 28px", fontSize: 12 }} data-testid="hero-open-magazine">
            {edition?.ui?.open || "Open the magazine"} <ArrowRight size={14} />
          </Link>
          <Link to={`/read/${lang}?p=3`} className="btn-silver" style={{ padding: "16px 24px", fontSize: 12 }} data-testid="hero-open-contents">{edition?.ui?.contents || "Contents"}</Link>
          <span className="text-xs opacity-50 ml-2">48 {edition?.ui?.pages || "pages"}</span>
        </motion.div>
      </div>
      <div className="lg:col-span-5 flex justify-center lg:justify-end" style={{ perspective: 1400 }}>
        <CoverCard cover={cover} />
      </div>
    </div>
  </section>
);
